import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const CLAVE_MENU = 'barbersaas.menu.colapsado';

/**
 * Logo de la cabecera del menu.
 *
 * Busca los archivos en este orden y usa el primero que exista:
 *   1. /isotipo.png  — solo la marca, para el menu plegado
 *   2. /logo.png     — el logotipo completo
 *   3. El texto "BarberSaaS", si no hay ninguno de los dos
 *
 * Va sobre una base blanca porque la barra es indigo: casi todos los logos
 * llevan tinta oscura y sobre ese fondo desaparecerian.
 */
function Marca({ colapsado }) {
  // 'isotipo' -> 'logo' -> 'texto'. Cada fallo pasa al siguiente.
  const [fuente, setFuente] = useState(colapsado ? 'isotipo' : 'logo');

  // Al plegar o desplegar se vuelve a intentar el archivo que toca.
  useEffect(() => { setFuente(colapsado ? 'isotipo' : 'logo'); }, [colapsado]);

  if (fuente === 'texto') {
    return (
      <>
        <div className="sidebar-logo-icon">💈</div>
        <div className="sidebar-logo-text">
          BarberSaaS
          <span>Panel de Control</span>
        </div>
      </>
    );
  }

  return (
    <div className="sidebar-logo-plate">
      <img
        className="sidebar-logo-img"
        src={fuente === 'isotipo' ? '/isotipo.png' : '/logo.png'}
        alt="AHA Solutions"
        onError={() => setFuente(f => (f === 'isotipo' ? 'logo' : 'texto'))}
      />
    </div>
  );
}

export function AppLayout({ title }) {
  const { user, logout, hasPlan } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // El menu plegado se recuerda entre pantallas y entre sesiones. Se lee dentro
  // del inicializador para no leer localStorage en cada render, y con try/catch
  // porque en navegacion privada el acceso puede lanzar.
  const [colapsado, setColapsado] = useState(() => {
    try { return localStorage.getItem(CLAVE_MENU) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(CLAVE_MENU, colapsado ? '1' : '0'); } catch { /* sin persistencia */ }
  }, [colapsado]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);

  const initials = user?.name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';

  // Navegación dinámica según rol y plan
  const buildNav = () => {
    if (user?.role === 'super_admin') {
      // Estructura completa del panel de administracion.
      // Los modulos aun no construidos abren una pantalla que explica que
      // hara ese modulo, en vez de un enlace roto o datos inventados.
      return [
        { label: '', items: [
          { path: '/superadmin', icon: '📊', label: 'Dashboard' },
        ]},
        { label: 'Plataforma', items: [
          { path: '/superadmin/businesses',    icon: '🏪', label: 'Negocios' },
          { path: '/superadmin/users',         icon: '👥', label: 'Usuarios' },
          { path: '/superadmin/subscriptions', icon: '💳', label: 'Suscripciones' },
          { path: '/superadmin/plans',         icon: '📋', label: 'Planes' },
          { path: '/superadmin/payments',      icon: '💲', label: 'Pagos' },
          { path: '/superadmin/billing',       icon: '🧾', label: 'Facturación' },
          { path: '/superadmin/commissions',   icon: '％', label: 'Comisiones' },
        ]},
        { label: 'Analitica', items: [
          { path: '/superadmin/growth',    icon: '📈', label: 'Crecimiento' },
          { path: '/superadmin/income',    icon: '💰', label: 'Ingresos' },
          { path: '/superadmin/expenses',  icon: '📉', label: 'Gastos' },
          { path: '/superadmin/usage',     icon: '🖥️', label: 'Uso de la Plataforma' },
          { path: '/superadmin/reports',   icon: '📄', label: 'Reportes' },
        ]},
        { label: 'Marketing', items: [
          { path: '/superadmin/campaigns',  icon: '📣', label: 'Campañas' },
          { path: '/superadmin/promotions', icon: '🎁', label: 'Promociones' },
        ]},
        { label: 'Soporte', items: [
          { path: '/superadmin/tickets',   icon: '🎫', label: 'Tickets' },
          { path: '/superadmin/knowledge', icon: '📚', label: 'Base de Conocimiento' },
        ]},
        { label: 'Configuracion', items: [
          { path: '/superadmin/settings',      icon: '⚙️', label: 'Parámetros' },
          { path: '/superadmin/integrations',  icon: '🔌', label: 'Integraciones' },
          { path: '/superadmin/security',      icon: '🛡️', label: 'Seguridad' },
          { path: '/superadmin/logs',          icon: '📜', label: 'Logs del Sistema' },
          { path: '/superadmin/backups',       icon: '☁️', label: 'Backups' },
        ]},
      ];
    }

    if (user?.role === 'barbero') {
      return [
        { label: 'Mi Agenda', items: [
          { path: '/barber/calendar',      icon: '📅', label: 'Calendario' },
          { path: '/barber/appointments',  icon: '✂️', label: 'Citas' },
        ]},
        { label: 'Soporte', items: [
          { path: '/barber/support', icon: '🎫', label: 'Soporte' },
        ]},
      ];
    }

    // admin y admin_general — filtrar por plan
    const operacion = [
      { path: '/admin/calendar', icon: '📅', label: 'Calendario' },
      { path: '/admin/appointments', icon: '✂️', label: 'Citas' },
      { path: '/admin/clients', icon: '👤', label: 'Clientes' },
    ].filter(Boolean);

    const configuracion = [
      { path: '/admin/services', icon: '💈', label: 'Servicios' },
      hasPlan('has_workers') && { path: '/admin/workers', icon: '👥', label: 'Trabajadores' },
      hasPlan('has_multi_branch') && { path: '/admin/branches', icon: '🏪', label: 'Sucursales' },
      { path: '/admin/hours', icon: '🕐', label: 'Horarios' },
      { path: '/admin/business', icon: '⚙️', label: 'Mi Negocio' },
      { path: '/admin/notifications', icon: '🔔', label: 'Notificaciones' },
    ].filter(Boolean);

    const finanzas = [
      hasPlan('has_finance') && { path: '/admin/finance', icon: '💰', label: 'Finanzas' },
    ].filter(Boolean);

    const analisis = [
      hasPlan('has_reports') && { path: '/admin/reports', icon: '📈', label: 'Reportes' },
      hasPlan('has_advanced_reports') && { path: '/admin/advanced-reports', icon: '📊', label: 'Reportes Avanzados' },
    ].filter(Boolean);

    // El Dashboard se muestra según el módulo del plan (Planes → Dashboard),
    // no según el nombre del plan. Así, si el superadmin activa o desactiva
    // "Dashboard" en el plan, el negocio lo ve o deja de verlo al refrescar.
    const sections = [
      ...(hasPlan('has_dashboard') ? [{ label: 'Dashboard', items: [
        { path: '/admin', icon: '📊', label: 'Dashboard' },
      ]}] : []),
      { label: 'Operacion', items: operacion },
      { label: 'Configuracion', items: configuracion },
    ];
    if (finanzas.length > 0) sections.push({ label: 'Finanzas', items: finanzas });
    if (analisis.length > 0) sections.push({ label: 'Analisis', items: analisis });
    sections.push({ label: 'Soporte', items: [
      { path: '/admin/support', icon: '🎫', label: 'Soporte' },
    ]});
    return sections;
  };

  const navSections = buildNav();

  return (
    <div className={`app-layout ${colapsado ? 'collapsed' : ''}`}>
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={closeSidebar} />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <Marca colapsado={colapsado} />
        </div>

        <nav className="sidebar-nav">
          {navSections.map(section => (
            <div key={section.label}>
              {/* Una seccion sin titulo (Dashboard) no muestra encabezado */}
              {section.label && <div className="sidebar-section-label">{section.label}</div>}
              {section.items.map(item => (
                <button
                  key={item.path}
                  className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => { navigate(item.path); closeSidebar(); }}
                  // Con el menu plegado solo se ve el icono: el title es lo
                  // unico que dice a donde lleva cada uno.
                  title={colapsado ? item.label : undefined}
                >
                  <span className="sidebar-icon">{item.icon}</span>
                  <span className="sidebar-link-text">{item.label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-user-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{user?.name}</div>
              <div className="sidebar-user-role">{user?.role?.replace(/_/g, ' ')}</div>
            </div>
          </div>
          <button className="sidebar-link" style={{ marginTop: 8 }} onClick={handleLogout}
                  title={colapsado ? 'Cerrar sesion' : undefined}>
            <span className="sidebar-icon">🚪</span>
            <span className="sidebar-link-text">Cerrar sesion</span>
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>☰</button>
          <button
            className="sidebar-toggle"
            onClick={() => setColapsado(c => !c)}
            title={colapsado ? 'Mostrar el menú' : 'Esconder el menú'}
            aria-label={colapsado ? 'Mostrar el menú' : 'Esconder el menú'}
          >{colapsado ? '»' : '«'}</button>
          <span className="topbar-title">{title || 'Panel'}</span>
          <div className="topbar-actions">
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
              {user?.business_name || 'BarberSaaS'}
            </span>
          </div>
        </header>
        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export default AppLayout;
